import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { PageTitle } from '../../../_metronic/layout/core';
import { DistributorModel, listDistributors } from '../../modules/distributors';
import {
  BillingStatusEnum,
  BulkActionsEnum,
  DeliveryNoteList,
  DeliveryNoteModel,
  DeliveryNoteStateEnum,
  listDeliveryNotes,
  listFiscalNames,
  performBulkAction,
  updateDeliveryNote,
  updateDeliveryNoteState,
  uploadEdiFile,
} from '../../modules/delivery-notes';
import { InvoiceKind, ModalInvoiceForm } from '../../modules/b2b-invoices';
import ModalDeliveryNoteEditable from '../../modules/delivery-notes/components/ModalDeliveryNoteEditable';
import { useQuery } from '@tanstack/react-query';
import DateRangeSelector from '../../components/DateRangeSelector';
import moment from 'moment';
import { listShippingServices } from '../../modules/orders';
import { ShippingService } from '../../modules/orders/models/ShippingService';

const DEBOUNCE_TIMEOUT = 800;
const initialSince = new Date();
const initialUntil = new Date(new Date().setDate(initialSince.getDate() + 7));

export function DeliveryNotesPage() {
  const intl = useIntl();
  const [distributors, setDistributors] = useState<Array<DistributorModel>>([]);
  const { data: fiscalNames, isLoading: isLoadingFiscalNames } = useQuery({
    queryKey: ['fiscalNames'],
    queryFn: listFiscalNames,
    select: ({ data }) =>
      data
        .map((item) => ({
          label: `${item.id} - ${item.name}`,
          value: item.id,
        }))
        .sort((a, b) => a.id - b.id),
  });
  const [externalReference, setExternalReference] = useState('');
  const [orderCode, setOrderCode] = useState('');
  const [billingAddressId, setBillingAddressId] = useState<number | null>(null);
  const [deliveryNotes, setDeliveryNotes] = useState<Array<DeliveryNoteModel>>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [selectedDistributor, setSelectedDistributor] =
    useState<DistributorModel | null>(null);
  const [billingStatus, setBillingStatus] = useState<BillingStatusEnum | null>(
    null
  );
  const [deliveryNoteStatus, setDeliveryNoteStatus] =
    useState<DeliveryNoteStateEnum | null>(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [since, setSince] = useState<Date | null>(initialSince);
  const [until, setUntil] = useState<Date | null>(initialUntil);
  const [minTotal, setMinTotal] = useState<number | null>(null);
  const [maxTotal, setMaxTotal] = useState<number | null>(null);
  const [shippingServices, setShippingServices] = useState<
    Array<ShippingService>
  >([]);
  const [selectedShippingService, setSelectedShippingService] = useState<
    number | null
  >(null);
  const [generateInvoiceModalVisible, setGenerateInvoiceModalVisible] =
    useState(false);
  const [deliveryNoteForInvoice, setDeliveryNoteForInvoice] =
    useState<DeliveryNoteModel | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingDeliveryNote, setEditingDeliveryNote] =
    useState<DeliveryNoteModel | null>(null);
  const billingStatusOptions = [
    {
      value: null,
      label: intl
        .formatMessage({ id: 'deliveryNotes.anyBillingStatus' })
        .toString(),
    },
    {
      value: BillingStatusEnum.INVOICED,
      label: BillingStatusEnum.INVOICED.toString(),
    },
    {
      value: BillingStatusEnum.PENDING,
      label: BillingStatusEnum.PENDING.toString(),
    },
  ];
  const deliveryNoteStatusOptions = [
    {
      value: null,
      label: intl
        .formatMessage({ id: 'deliveryNotes.anyBillingStatus' })
        .toString(),
    },
    ...Object.keys(DeliveryNoteStateEnum).map((key) => ({
      value: DeliveryNoteStateEnum[key],
      label: DeliveryNoteStateEnum[key],
    })),
  ];
  const distributorOptions = [
    {
      value: '',
      label: intl.formatMessage({ id: 'deliveryNotes.anyDistributor' }),
    },
    ...distributors.map((distributor) => ({
      value: distributor.id,
      label: distributor.name,
    })),
  ];

  const shippingServiceOptions = [
    {
      value: null,
      label: intl.formatMessage({ id: 'deliveryNotes.anyShippingService' }),
    },
    ...shippingServices.map((service) => ({
      value: service.id,
      label: service.name,
    })),
  ];

  const fetchDistributors = async () => {
    try {
      const { data } = await listDistributors('active');
      setDistributors(data);
    } catch (error) {
      console.warn(error);
    }
  };

  const fetchShippingServices = async () => {
    try {
      const { data } = await listShippingServices();
      setShippingServices(data);
    } catch (error) {
      console.warn(error);
    }
  };

  const fetchDeliveryNotes = async (withLoader = true) => {
    try {
      if (withLoader) {
        setLoading(true);
      }
      const { data } = await listDeliveryNotes({
        distributorId: selectedDistributor?.id,
        billingStatus,
        billingAddressId,
        page,
        since: since ? moment(since).format('YYYY-MM-DD') : undefined,
        until: until ? moment(until).format('YYYY-MM-DD') : undefined,
        deliveryNoteStatus,
        orderCode,
        externalReference,
        minTotal,
        maxTotal,
        shippingService: selectedShippingService,
      });
      setDeliveryNotes(data.results);
      setCount(data.count);
    } catch (error) {
      toast.error(error.message);
    } finally {
      if (withLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchDistributors();
    fetchShippingServices();
  }, []);

  const fetchDependencies = [
    selectedDistributor,
    billingStatus,
    billingAddressId,
    since,
    until,
    deliveryNoteStatus,
    externalReference,
    minTotal,
    maxTotal,
    orderCode,
    selectedShippingService,
  ];

  useEffect(() => {
    setPage(1);
    setCount(0);
  }, fetchDependencies);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchDeliveryNotes();
    }, DEBOUNCE_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  }, [page, ...fetchDependencies]);

  async function onUpdateState(
    order: DeliveryNoteModel,
    state: DeliveryNoteStateEnum
  ) {
    try {
      await updateDeliveryNoteState({
        ...order,
        state,
      });
      fetchDeliveryNotes(false);
      toast.success(
        intl.formatMessage({ id: 'deliveryNotes.updateStateSuccess' })
      );
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'deliveryNotes.updateStateError' }));
      console.warn(error);
    }
  }

  function onGenerateInvoice(deliveryNote: DeliveryNoteModel) {
    setDeliveryNoteForInvoice(deliveryNote);
    setGenerateInvoiceModalVisible(true);
  }

  async function onUpdateDeliveryNote({ deliveryNoteId, request }) {
    try {
      setLoading(true);
      setEditModalVisible(false);

      const { data: updatedDeliveryNote } = await updateDeliveryNote({
        deliveryNoteId,
        request,
      });

      const index = deliveryNotes.findIndex((d) => d.id === deliveryNoteId);
      const newDeliveryNotes = [
        ...deliveryNotes.slice(0, index),
        {
          ...updatedDeliveryNote,
          delivery_note_lines: deliveryNotes[index].delivery_note_lines.map(
            (line) => ({
              ...line,
              served_units: updatedDeliveryNote.delivery_note_lines.find(
                (l) => l.id === line.id
              )?.served_units,
            })
          ),
        },
        ...deliveryNotes.slice(index + 1),
      ];

      setDeliveryNotes(newDeliveryNotes);
      toast.success(intl.formatMessage({ id: 'deliveryNotes.updateSuccess' }));
    } catch (error) {
      console.warn(error);
      toast.error(intl.formatMessage({ id: 'deliveryNotes.updateError' }));
    } finally {
      setLoading(false);
    }
  }

  function onEdit(deliveryNote: DeliveryNoteModel) {
    setEditingDeliveryNote(deliveryNote);
    setEditModalVisible(true);
  }

  async function onUploadEdi(deliveryNote: DeliveryNoteModel) {
    const confirmMessage = deliveryNote.edi_uploaded_at
      ? intl.formatMessage(
          { id: 'edi.uploadAgainConfirm' },
          {
            code: deliveryNote.code,
            date: new Date(deliveryNote.edi_uploaded_at).toLocaleString(),
          }
        )
      : intl.formatMessage(
          { id: 'edi.uploadConfirm' },
          { code: deliveryNote.code }
        );
    if (!window.confirm(confirmMessage)) return;
    try {
      const { data } = await uploadEdiFile(deliveryNote.id);
      setDeliveryNotes((current) =>
        current.map((item) =>
          item.id === deliveryNote.id ? { ...item, ...data } : item
        )
      );
      toast.success(intl.formatMessage({ id: 'edi.uploadSuccess' }));
    } catch (error) {
      console.warn(error);
      // The backend answers a rejected upload with ["<Ediversa reason>"].
      const reason = error?.response?.data;
      toast.error(
        intl.formatMessage(
          { id: 'edi.uploadError' },
          { reason: Array.isArray(reason) ? reason[0] : '' }
        )
      );
    }
  }

  async function onPerformBulkAction(
    action: BulkActionsEnum,
    deliveryNoteIds: number[]
  ) {
    return performBulkAction({
      action,
      deliveryNoteIds,
      distributor: selectedDistributor?.id || null,
      billing_status: billingStatus || null,
      start_date: since ? moment(since).format('YYYY-MM-DD') : null,
      end_date: until ? moment(until).format('YYYY-MM-DD') : null,
      delivery_note_status: deliveryNoteStatus || null,
      order_code: orderCode || null,
      external_ref: externalReference || null,
      min_total: minTotal || null,
      max_total: maxTotal || null,
      fiscal_name_id: billingAddressId || null,
      state: deliveryNoteStatus || null,
      shipping_service: selectedShippingService || null,
    });
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'menu.deliveryNotes' })}
      </PageTitle>
      <div className="row">
        <div className="">
          <DeliveryNoteList
            loading={loading}
            deliveryNotes={deliveryNotes}
            count={count}
            page={page}
            onPageChange={setPage}
            onUpdateState={onUpdateState}
            onGenerateInvoice={onGenerateInvoice}
            onEdit={onEdit}
            onUploadEdi={onUploadEdi}
            onPerformBulkAction={onPerformBulkAction}
          >
            <div className="d-flex flex-wrap gap-4">
              <DateRangeSelector
                initialSince={since}
                initialUntil={until}
                onSelected={(since, until) => {
                  setSince(since);
                  setUntil(until);
                }}
                disableFutureDates={false}
              />
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.selectDistributor',
                  })}
                </label>
                <Select
                  className="react-select-container w-300px"
                  classNamePrefix="react-select"
                  options={distributorOptions}
                  value={distributorOptions?.find(
                    (d) => d.value === selectedDistributor?.id || !d.value
                  )}
                  onChange={(e) => {
                    const distributorId = e?.value;
                    const distributor = distributors.find(
                      (d) => d.id === distributorId
                    );
                    setSelectedDistributor(distributor);
                  }}
                  placeholder={intl.formatMessage({ id: 'planDetail.select' })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.selectBillingStatus',
                  })}
                </label>
                <Select
                  className="react-select-container w-300px"
                  classNamePrefix="react-select"
                  options={billingStatusOptions}
                  value={billingStatusOptions.find(
                    (d) => d.value == billingStatus
                  )}
                  onChange={(e) => {
                    setBillingStatus(
                      e?.value ? (e.value as BillingStatusEnum) : null
                    );
                  }}
                  placeholder={intl.formatMessage({ id: 'planDetail.select' })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.selectDeliveryNoteStatus',
                  })}
                </label>
                <Select
                  className="react-select-container w-300px"
                  classNamePrefix="react-select"
                  options={deliveryNoteStatusOptions}
                  value={deliveryNoteStatusOptions.find(
                    (d) => d.value == deliveryNoteStatus
                  )}
                  onChange={(e) => {
                    setDeliveryNoteStatus(
                      e?.value ? (e.value as DeliveryNoteStateEnum) : null
                    );
                  }}
                  placeholder={intl.formatMessage({ id: 'planDetail.select' })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.selectFiscalName',
                  })}
                </label>
                <Select
                  isDisabled={isLoadingFiscalNames}
                  className="react-select-container w-300px"
                  classNamePrefix="react-select"
                  options={fiscalNames}
                  value={fiscalNames?.find((d) => d.value == billingAddressId)}
                  onChange={(e) => {
                    setBillingAddressId(
                      e?.value ? parseInt(e.value, 10) : null
                    );
                  }}
                  placeholder={intl.formatMessage({ id: 'planDetail.select' })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.externalReference',
                  })}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={intl.formatMessage({
                    id: 'deliveryNotes.externalReferencePlaceholder',
                  })}
                  value={externalReference}
                  onChange={(e) => setExternalReference(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.minTotal',
                  })}
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    placeholder={intl.formatMessage({
                      id: 'deliveryNotes.minTotalPlaceholder',
                    })}
                    value={minTotal}
                    onChange={(e) => setMinTotal(parseInt(e.target.value, 10))}
                  />
                  <span className="input-group-text">€</span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.maxTotal',
                  })}
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    placeholder={intl.formatMessage({
                      id: 'deliveryNotes.maxTotalPlaceholder',
                    })}
                    value={maxTotal}
                    onChange={(e) => setMaxTotal(parseInt(e.target.value, 10))}
                  />
                  <span className="input-group-text">€</span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.orderCode',
                  })}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={intl.formatMessage({
                    id: 'deliveryNotes.orderCodePlaceholder',
                  })}
                  value={orderCode}
                  onChange={(e) => setOrderCode(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.selectShippingService',
                  })}
                </label>
                <Select
                  className="react-select-container w-300px"
                  classNamePrefix="react-select"
                  options={shippingServiceOptions}
                  value={shippingServiceOptions.find(
                    (s) => s.value === selectedShippingService
                  )}
                  onChange={(e) => {
                    setSelectedShippingService(e?.value || null);
                  }}
                  placeholder={intl.formatMessage({ id: 'planDetail.select' })}
                />
              </div>
            </div>
          </DeliveryNoteList>
          <ModalInvoiceForm
            visible={generateInvoiceModalVisible}
            onClose={() => setGenerateInvoiceModalVisible(false)}
            order={{
              id: deliveryNoteForInvoice?.order,
              code: deliveryNoteForInvoice?.order,
            }}
            invoiceKind={InvoiceKind.Invoice}
          />
          <ModalDeliveryNoteEditable
            visible={editModalVisible}
            onClose={() => setEditModalVisible(false)}
            deliveryNote={editingDeliveryNote}
            loading={loading}
            onUpdateDeliveryNote={onUpdateDeliveryNote}
          />
        </div>
      </div>
    </>
  );
}
